import {
  BankOutlined,
  BarChartOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  FileOutlined,
  SearchOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Row,
  Space,
  Table,
  TextField,
  Typography,
  useForm,
} from "@pankod/refine-antd";
import { HttpError, useMutation } from "@pankod/refine-core";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs, { Dayjs } from "dayjs";
import { useFunnelData } from "hooks/useFunnelData";
import React, { useMemo } from "react";
import { Chart as ChartJSComponent } from "react-chartjs-2";
import {
  budgetsByDate,
  topAggregateSalesByDate,
  totalBudgetByDate,
  totalTouchedLivesByDate,
} from "services/dashboard";
import { getMaxRounded, stepCalculator } from "utils/utils";

// REGISTRO GLOBAL DOS CONTROLLERS (fora do componente)
ChartJS.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Legend,
  Tooltip,
  ChartDataLabels
);

interface DashboardData {
  totalBudgets: number;
  totalLives: number;
  totalPaidBudgets: number;
  conversionRate: number;
  budgetsByDateRange: any[];
  top10HealthInsurer: any[];
  top10BranchOffices: any[];
}
interface IDashboardFilterForm {
  start_date: Dayjs;
  end_date: Dayjs;
}

const emptyDashboardData: DashboardData = {
  totalBudgets: 0,
  totalLives: 0,
  totalPaidBudgets: 0,
  conversionRate: 0,
  budgetsByDateRange: [],
  top10HealthInsurer: [],
  top10BranchOffices: [],
};

export const DashboardV2: React.FC = () => {
  const { formProps: searchFormProps } = useForm<
    any,
    HttpError,
    IDashboardFilterForm
  >();

  const {
    data: dashboardData = emptyDashboardData,
    isLoading,
    mutate: getDashboardData,
    reset,
  } = useMutation({
    mutationFn: async (dateRange: {
      start_date: string;
      end_date: string;
    }): Promise<DashboardData> => {
      const budgets = await budgetsByDate(dateRange);
      const totalBudgets = await totalBudgetByDate(dateRange);
      const totalLives = await totalTouchedLivesByDate(dateRange);

      const top10HealthInsurer = await topAggregateSalesByDate({
        ...dateRange,
        field: "HealthInsurer",
      });

      const top10BranchOffices = await topAggregateSalesByDate({
        ...dateRange,
        field: "BranchOffice",
      });

      const paidBudgets = await totalBudgetByDate({
        ...dateRange,
        status: 9,
      });

      const conversionRate =
        totalBudgets > 0 ? ((paidBudgets || 0) / totalBudgets) * 100 : 0;

      return {
        totalBudgets: totalBudgets || 0,
        totalLives: totalLives.totalAmount || 0,
        totalPaidBudgets: paidBudgets || 0,
        conversionRate,
        budgetsByDateRange: budgets || [],
        top10HealthInsurer: top10HealthInsurer || [],
        top10BranchOffices: top10BranchOffices || [],
      };
    },
  });

  useMemo(() => {
    const today = dayjs();

    searchFormProps.form?.setFieldsValue({
      start_date: today,
      end_date: today,
    });

    searchFormProps.form?.submit();
  }, [searchFormProps.form]);

  const funnelData = useFunnelData(dashboardData);
  const labels = funnelData.map((i) => i.etapa);
  const valorAcumulado = funnelData.map((i) => i.gerado);
  const maxValorAcumulado = Math.max(...valorAcumulado) * 1.2;
  const taxaAbandono = funnelData.map((i) => i.abandono);

  const cards = [
    {
      value: dashboardData?.totalBudgets.toLocaleString("pt-BR"),
      label: "Total de Leads",
      desc: "Soma de orçamentos no período",
      icon: <FileOutlined style={{ fontSize: 32 }} />,
      style: { background: "#003D85", color: "#fff" },
    },
    {
      value: dashboardData?.totalLives.toLocaleString("pt-BR"),
      label: "Total de vidas",
      desc: "Soma de titulares com direito e dependentes",
      icon: <UsergroupAddOutlined style={{ fontSize: 32 }} />,
      style: {},
    },
    {
      value: dashboardData?.totalPaidBudgets.toLocaleString("pt-BR"),
      label: "Total de orçamentos Pagos",
      desc: "Orçamentos com status 9 (processado) no SIGO",
      icon: <CreditCardOutlined style={{ fontSize: 32 }} />,
      style: {},
    },
    {
      value: `${dashboardData?.conversionRate.toFixed(1)}%`,
      label: "Taxa de conversão",
      desc: "Orçamentos com status 9 (processado) no SIGO",
      icon: <BarChartOutlined style={{ fontSize: 32 }} />,
      style: {},
    },
  ];

  const data = {
    labels,
    datasets: [
      {
        type: "line" as const,
        label: "Taxa de Abandono",
        data: taxaAbandono,
        borderColor: "#F5A623",
        backgroundColor: "#F5A623",
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#F5A623",
        pointBorderColor: "#fff",
        fill: false,
        yAxisID: "y2",
        datalabels: {
          color: "#F5A623",
          font: { size: 14 },
          formatter: function (value: number) {
            return value + "%";
          },
          display: true,
        },
      },
      {
        type: "bar" as const,
        label: "Valor Acumulado",
        data: valorAcumulado,
        backgroundColor: "#003D85",
        borderRadius: 6,
        yAxisID: "y1",
        barPercentage: 0.7,
        categoryPercentage: 0.7,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    layout: {
      padding: {
        top: 50,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        align: "start",
        labels: {
          usePointStyle: true,
          font: { size: 16 },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const label = ctx.dataset.label || "";

            if (label === "Taxa de Abandono") {
              const index = ctx.dataIndex;
              const totalAbandono = funnelData[index]?.abandono_total ?? 0;
              const taxa = ctx.formattedValue;

              return `${label}: ${totalAbandono.toLocaleString("pt-BR")} / ${taxa}%`;
            }

            if (label === "Valor Acumulado") {
              return `${label}: ${ctx.formattedValue.toLocaleString("pt-BR")}`;
            }

            return `${label}: ${ctx.formattedValue}`;
          },
        },
      },
      datalabels: {
        color: "#0B3D91",
        anchor: "end",
        align: "end",
        offset: -4,
        formatter: (value: number, context: any) => {
          if (context.dataset.type !== "bar") return null;
          return value > 0 ? value.toLocaleString("pt-BR") : null;
        },
      },
    },
    scales: {
      y1: {
        type: "linear",
        position: "left",
        min: 0,
        max: getMaxRounded(maxValorAcumulado),
        title: { display: true, text: "Valor acumulado" },
        ticks: {
          stepSize: stepCalculator(maxValorAcumulado),
          callback: (value: number) => value.toLocaleString("pt-BR"),
        },
        grid: { color: "#eaeaea" },
      },
      y2: {
        type: "linear",
        position: "right",
        min: 0,
        max: 100,
        title: { display: true, text: "Taxa de abandono" },
        ticks: {
          stepSize: 20,
          callback: (value: any) => `${value}%`,
        },
        grid: { drawOnChartArea: false },
      },
    },
  };

  const validateDatePair =
    (
      field: "start_date" | "end_date",
      otherField: "start_date" | "end_date",
      form?: FormInstance<IDashboardFilterForm>
    ) =>
    async (_: any, value: any) => {
      const otherValue = form?.getFieldValue(otherField);

      if (!value && !otherValue) return Promise.resolve();

      if (value && !otherValue)
        return Promise.reject(
          otherField === "end_date"
            ? "Informe a data final"
            : "Informe a data inicial"
        );

      if (value && otherValue) {
        const startDate = field === "start_date" ? value : otherValue;
        const endDate = field === "end_date" ? value : otherValue;

        const diff = dayjs(endDate).diff(dayjs(startDate), "day");

        if (diff < 0) {
          return Promise.reject(
            "A data final não pode ser anterior à data inicial"
          );
        }

        if (diff >= 30) {
          return Promise.reject(
            "A diferença entre data inicial e final não pode ser maior que 30 dias"
          );
        }
      }

      return Promise.resolve();
    };

  const handleFinish = (dateRange: IDashboardFilterForm) => {
    const { start_date, end_date } = dateRange;

    getDashboardData({
      start_date: dayjs(start_date).format("YYYY-MM-DD"),
      end_date: dayjs(end_date).format("YYYY-MM-DD"),
    });
  };

  const handleClear = () => {
    searchFormProps.form?.resetFields();
    reset();
  };

  const getDisplayDates = () => {
    const { start_date, end_date } =
      searchFormProps.form?.getFieldsValue() || {};

    return {
      start_date: dayjs(start_date).format("DD/MM/YYYY"),
      end_date: dayjs(end_date).format("DD/MM/YYYY"),
    };
  };

  const getCardBackground = (index: number) => {
    switch (true) {
      case index === 0:
        return "#fff";
      case [1, 2, 3].includes(index): {
        return "##003D85";
      }
      default:
        return "#rgba(0,61,133,0.07)";
    }
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Card
        title={<Typography.Title level={3}>Dashboard</Typography.Title>}
        style={{ borderRadius: 20 }}
        bodyStyle={{ padding: 32 }}
      >
        <Form {...searchFormProps} onFinish={handleFinish} layout="horizontal">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label={<TextField value="Data inicial" />}
                name="start_date"
                rules={[
                  {
                    validator: validateDatePair(
                      "start_date",
                      "end_date",
                      searchFormProps?.form
                    ),
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Data inicial"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={<TextField value="Data final" />}
                name="end_date"
                rules={[
                  {
                    validator: validateDatePair(
                      "end_date",
                      "start_date",
                      searchFormProps?.form
                    ),
                  },
                  {
                    validator: (_, value) => {
                      const startDate =
                        searchFormProps.form?.getFieldValue("start_date");

                      const start = dayjs(startDate);
                      const end = dayjs(value);

                      if (start.isAfter(end)) {
                        return Promise.reject(
                          "A data final não pode ser menor que a data inicial."
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Data final"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>

            <Col xs={24} style={{ textAlign: "right" }}>
              <Space>
                <Button
                  icon={<DeleteOutlined />}
                  style={{ minWidth: 100 }}
                  onClick={handleClear}
                >
                  Limpar
                </Button>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  style={{ minWidth: 100 }}
                  htmlType="submit"
                  loading={isLoading}
                >
                  Buscar
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>
      <Row gutter={24}>
        {cards.map((card, idx) => (
          <Col span={6} key={card.label}>
            <Card
              style={{
                borderRadius: 20,
                height: 190,
                boxShadow: "0 2px 8px #0000000d",
                padding: 0,
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                ...card.style,
              }}
              bodyStyle={{ padding: 28, height: "100%" }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  right: 14,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: getCardBackground(idx),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                }}
              >
                {React.cloneElement(card.icon, {
                  style: {
                    fontSize: 20,
                    color: [1, 2, 3].includes(idx) ? "#fff" : "#003D85",
                  },
                })}
              </div>
              <div>
                <Typography.Title
                  level={1}
                  style={{
                    margin: 0,
                    fontSize: 48,
                    color: idx === 0 ? "#fff" : "#003D85",
                    fontWeight: 700,
                    lineHeight: 1.1,
                  }}
                >
                  {card.value}
                </Typography.Title>
                <Typography.Text
                  style={{
                    display: "block",
                    fontSize: 18,
                    fontWeight: 600,
                    color: idx === 0 ? "#fff" : "#003D85",
                  }}
                >
                  {card.label}
                </Typography.Text>
                <div
                  style={{
                    fontSize: 12,
                    color: idx === 0 ? "#e6f0fa" : "#6b7a90",
                    marginTop: 2,
                  }}
                >
                  {card.desc}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      <Card
        style={{
          marginBottom: 32,
          borderRadius: 20,
          boxShadow: "0 2px 8px #0000000d",
          padding: 0,
        }}
        bodyStyle={{ padding: 20 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 8,
          }}
        >
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Gráfico Funil
            </Typography.Title>
            <Typography.Text style={{ color: "#444" }}>
              O gráfico apresenta a taxa de abandono por etapa do funil de
              contratação com base nos orçamentos gerados no período
              selecionado.
            </Typography.Text>
          </div>
          <div>
            <Typography.Title level={4} style={{ margin: 0, color: "#444" }}>
              {(() => {
                const { start_date, end_date } = getDisplayDates();
                return `${start_date} - ${end_date}`;
              })()}
            </Typography.Title>
          </div>
        </div>
        <div style={{ height: 420, width: "100%" }}>
          <ChartJSComponent type="bar" data={data} options={options} />
        </div>
      </Card>
      <Row gutter={16}>
        <Col span={13}>
          <Card
            className="top10-cities-card"
            title={
              <div className="card-title-row">
                <span className="card-title-text">
                  Top 10 orçamentos por cidades
                </span>
                <span className="card-title-icon">
                  <EnvironmentOutlined />
                </span>
              </div>
            }
            bodyStyle={{ padding: 0 }}
          >
            <Table
              dataSource={dashboardData?.top10BranchOffices.map(
                (item: any, index: number) => ({
                  key: index + 1,
                  data: (() => {
                    const { start_date, end_date } = getDisplayDates();
                    return `${start_date} - ${end_date}`;
                  })(),
                  cidade: item.branchName || `Cidade ${index + 1}`,
                  orcamentos: item.count || 0,
                })
              )}
              columns={[
                { title: "Data", dataIndex: "data", key: "data", width: 100 },
                {
                  title: "Cidades",
                  dataIndex: "cidade",
                  key: "cidade",
                  width: 140,
                },
                {
                  title: "Orçamentos gerados",
                  dataIndex: "orcamentos",
                  key: "orcamentos",
                  width: 100,
                  align: "right",
                  render: (value: number) => value.toLocaleString("pt-BR"),
                },
              ]}
              pagination={false}
              size="small"
              style={{ marginTop: 16 }}
            />
          </Card>
        </Col>
        <Col span={11}>
          <Card
            className="top10-cities-card"
            title={
              <div className="card-title-row">
                <span className="card-title-text">Orçamento por operadora</span>
                <span className="card-title-icon">
                  <BankOutlined />
                </span>
              </div>
            }
            bodyStyle={{ padding: 0 }}
          >
            <Table
              dataSource={dashboardData?.top10HealthInsurer.map(
                (item: any, index: number) => ({
                  key: index + 1,
                  data: (() => {
                    const { start_date, end_date } = getDisplayDates();
                    return `${start_date} - ${end_date}`;
                  })(),
                  operadora: item.healthInsurer || `Operadora ${index + 1}`,
                  orcamentos: item.count || 0,
                })
              )}
              columns={[
                { title: "Data", dataIndex: "data", key: "data", width: 100 },
                {
                  title: "Operadora",
                  dataIndex: "operadora",
                  key: "operadora",
                  width: 90,
                },
                {
                  title: "Orçamentos gerados",
                  dataIndex: "orcamentos",
                  key: "orcamentos",
                  width: 100,
                  align: "right",
                  render: (value: number) => value.toLocaleString("pt-BR"),
                },
              ]}
              pagination={false}
              size="small"
              style={{ marginTop: 16 }}
            />
          </Card>
        </Col>
      </Row>
    </Space>
  );
};
